import { useNow } from "@/hooks/useMotion";
import { timeAgo } from "@/lib/dates";
import { formatChurchTime, formatClock, formatVisitorTime, nextService, startsIn } from "@/lib/schedule";
import { WEEKDAY_NAMES, WEEKLY_SERVICES } from "@/content/schedule";
import { useLive } from "@/state/LiveProvider";
import { Link } from "@/router/Router";
import { Button } from "@/components/ui/Button";
import { Icon } from "@/components/ui/Icon";
import { Badge, Container, Eyebrow } from "@/components/ui/Primitives";
import { Reveal } from "@/components/motion/Reveal";
import { MediaPlayer } from "@/components/media/MediaPlayer";

/**
 * Live experience. When GET /api/app/live says isLive, a prominent "We are live" card with the
 * stream. Otherwise the next service and the weekly rhythm — never a pretend live state.
 */
export function LiveExperience() {
  const { status, loading } = useLive();
  const now = useNow(30_000);
  const next = nextService(now);
  const live = status?.isLive === true && status.streamUrl;
  // When not live, the hero's "What's happening" cards already show the next service.
  if (!live) return null;

  return (
    <section id="live" className="live-section" aria-labelledby="live-title">
      <Container>
        {live ? (
          <Reveal className="live-card">
            <div className="live-card__glow" aria-hidden />
            <div className="live-card__text">
              <Badge tone="live">Live now</Badge>
              <h2 id="live-title" className="live-card__title">
                We are live.
              </h2>
              <p className="live-card__name">{status.title}</p>
              {status.startedAt ? <p className="live-card__meta">Started {timeAgo(status.startedAt)}</p> : null}
              <div className="live-card__actions">
                <Button to="/watch/live" variant="flame" size="lg" icon="radio">
                  Watch live
                </Button>
              </div>
            </div>
            <div className="live-card__player">
              <MediaPlayer url={status.streamUrl!} type="video" title={status.title} />
            </div>
          </Reveal>
        ) : (
          <div className="next-service">
            <Reveal className="next-service__main">
              <Eyebrow tone="leaf">{loading ? "Checking for a live service" : next?.inProgress ? "In church now" : "Join us this week"}</Eyebrow>
              <h2 id="live-title" className="next-service__title">
                {next ? (
                  <>
                    {next.inProgress ? "Happening now:" : "Next:"} <em>{next.service.name}</em>
                  </>
                ) : (
                  "Join us this week"
                )}
              </h2>
              {next ? (
                <p className="next-service__when">
                  <Icon name="clock" size={18} />
                  <span>
                    {formatChurchTime(next.startsAt)}
                    {!next.inProgress ? <span className="next-service__in"> · {startsIn(next.startsAt, now)}</span> : null}
                    {formatVisitorTime(next.startsAt) ? <span className="next-service__local">{formatVisitorTime(next.startsAt)}</span> : null}
                  </span>
                </p>
              ) : null}
              <p className="next-service__note">
                {status?.offlineMessage ?? "Services stream live here and on YouTube. When we go live, this page lights up."}
              </p>
              <div className="next-service__actions">
                <Button to="/locations" variant="primary" icon="pin">
                  Find a location
                </Button>
                <Button to="/watch/sermons" variant="ghost" icon="play">
                  Watch past services
                </Button>
              </div>
            </Reveal>
            <Reveal className="next-service__week" delay={120}>
              <p className="next-service__week-title">Weekly rhythm</p>
              <ul>
                {WEEKLY_SERVICES.map((s) => (
                  <li key={s.id} className={next?.service.id === s.id ? "is-next" : undefined}>
                    <span className="next-service__day">{WEEKDAY_NAMES[s.weekday].slice(0, 3)}</span>
                    <span className="next-service__svc">{s.name}</span>
                    <span className="next-service__time">{formatClock(s.start)}</span>
                  </li>
                ))}
              </ul>
              <Link to="/calendar" className="text-link">
                Full church calendar <Icon name="arrowRight" size={15} />
              </Link>
            </Reveal>
          </div>
        )}
      </Container>
    </section>
  );
}
